import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  VoucherPayment,
  PaginatedVoucherPaymentResponse,
} from 'src/models/VoucherPayment.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';

export const useGetVoucherPayments = () => {
  const [payments, setPayments] = useState<VoucherPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchPayments = async (
    voucherId: string,
    page = 1,
    limit = 15,
    sortBy = 'paymentDate',
    order = 'desc'
  ) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        voucherId: voucherId,
        offset: page,
        limit: limit,
        sortBy: sortBy,
        order: order,
      };

      const response =
        await axiosTokenInstance.get<PaginatedVoucherPaymentResponse>(
          apiUrls.GET_VOUCHER_PAYMENTS,
          {
            params,
            withCredentials: true,
          }
        );

      const mappedPayments = (response.data.data || []).map(
        (payment: VoucherPayment) => ({
          ...payment,
          voucherId: payment.voucherId,
        })
      );

      setPayments(mappedPayments);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(page);
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (err.response?.status === 404) {
        console.error(err.response.data?.message);
        setPayments([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deletePayment = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        apiUrls.DELETE_VOUCHER_PAYMENT,
        {
          params: { id },
          withCredentials: true,
        }
      );

      setPayments((prevPayments) =>
        prevPayments.filter((payment) => payment.id !== id)
      );

      if (payments.length === 1) {
        setPayments([]);
      }
      showSnackbar(response.data.message, 'success');
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const fetchPaymentById = async (paymentId: string) => {
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_VOUCHER_PAYMENT_BY_ID,
        {
          params: { id: paymentId },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: unknown) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      throw error;
    }
  };

  return {
    payments,
    loading,
    totalItems,
    totalPages,
    currentPage,
    fetchPayments,
    deletePayment,
    fetchPaymentById,
  };
};
